import { useQueryClient } from '@tanstack/react-query';
import { type ReactNode, useEffect } from 'react';
import { Navigate, useLocation } from 'react-router';
import { AuthFrame } from '../features/auth/AuthFrame';
import { UNAUTHORIZED_EVENT } from '../lib/api';
import { useAuthState } from '../lib/auth';
import { keys } from '../lib/query';
import { Button, Spinner } from '../ui';

function Splash() {
  return (
    <div className="flex min-h-dvh items-center justify-center bg-paper text-ink-3">
      <Spinner className="h-6 w-6" />
    </div>
  );
}

/** Mostra l'app solo con una sessione valida; altrimenti porta all'accesso ricordando la pagina. */
export function RequireAuth({ children }: { children: ReactNode }) {
  const auth = useAuthState();
  const location = useLocation();
  const qc = useQueryClient();

  useEffect(() => {
    const onUnauthorized = () => qc.invalidateQueries({ queryKey: keys.auth });
    window.addEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
    return () => window.removeEventListener(UNAUTHORIZED_EVENT, onUnauthorized);
  }, [qc]);

  if (auth.isLoading) return <Splash />;
  if (auth.isError && !auth.data) {
    return (
      <AuthFrame
        title="Non riesco a collegarmi"
        subtitle="Il server di Regia non risponde. Controlla la connessione."
      >
        <Button block onClick={() => auth.refetch()}>
          Riprova
        </Button>
      </AuthFrame>
    );
  }
  if (auth.data?.needsSetup) {
    return (
      <AuthFrame title="Benvenuto in Regia" subtitle="Manca solo il primo accesso.">
        <p className="text-[0.9375rem] text-ink-2">
          Apri il link di primo accesso che hai ricevuto: lì scegli la password e colleghi il
          telefono. Il link vale 24 ore.
        </p>
      </AuthFrame>
    );
  }
  if (!auth.data?.user) {
    const next = `${location.pathname}${location.search}`;
    return (
      <Navigate
        to={next === '/' ? '/accedi' : `/accedi?next=${encodeURIComponent(next)}`}
        replace
      />
    );
  }
  return <>{children}</>;
}
